import { Box, Button, Divider, Typography, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { Business_Profile_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { contractAtom, currentContextAtom } from '../state';
import { useCompanyByIdQuery } from '../queries/__generated__/companyById.generated';
import { MinimumOrderValue } from '../common/state/state';
import { formatCompanyAddress, getDeliveryZoneFromAdministrativeArea, getMinimumOrderValueFromDeliveryZones } from '../common/utils/common.utils';
import Sheet from '../common/components/Sheet';
import SizedImage from '../common/components/SizedImage';
import FileUtils from '../common/utils/file.utils';
import InfoSocialTags from '../social-proof/InfoSocialTags';
import { PackagingFamilyTypeIntl } from '../common/i18n/product-family.translation';
import DiscoveryDialogPackagings from '../discovery/as-buyer/DiscoveryDialogPackagings';
import CompanyServicesInfoLight from '../company/companyInfo/CompanyServicesInfoLight';
import SocialTagCard from '../social-proof/SocialTagCard';
import { RoutePaths } from '../routes/AppRoutes';
import WishlistSupplierButton from '../wishlist/WishlistSupplierButton';
import ContractRequestButton from '../company/ContractRequestButton';
import ResumeNegotiationButton from '../company/ResumeNegotiationButton';
import useRelevantSocialTags from '../social-proof/hooks/useRelevantSocialTags';

interface SupplierSheetDrawerProps {
  companyId: string;
  isOpen: boolean;
  close: () => void;
}

export default function SupplierSheetDrawer({ close, isOpen, companyId }: SupplierSheetDrawerProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'discovery', 'search', 'products', 'contracts', 'product-family']);
  const navigate = useNavigate();
  const context = useAtomValue(currentContextAtom);

  const companyContractAtom = useMemo(() => {
    return contractAtom(context.companyId, companyId);
  }, [context, companyId]);

  const contract = useAtomValue(companyContractAtom);

  const { data, loading: isLoading } = useCompanyByIdQuery({
    variables: {
      companyId: companyId!,
      getProductSubFamilies: true,
    },
    skip: !isOpen,
  });

  const company = data?.company_by_pk;

  const targetGeoLoc = useMemo(() => {
    if (!company?.addresses?.[0]) {

      return;
    }

    return { lat: company.addresses[0].latitude, lng: company.addresses[0].longitude };
  }, [company]);

  const mov: MinimumOrderValue | undefined = useMemo(() => {
    if (!company) {

      return;
    }

    const movFromDeliveryZone = getMinimumOrderValueFromDeliveryZones(company.delivery_zones, context.companyAddress.administrativeAreaLevel2 || '');

    if (contract?.minimum_order_value) {

      return {
        type: contract.minimum_order_value_type,
        value: contract.minimum_order_value,
        negotiable: movFromDeliveryZone?.negotiable,
      };
    }

    return movFromDeliveryZone;
  }, [company, contract, context]);

  const deliveryZone = useMemo(() => {
    if (!company) {

      return;
    }

    return getDeliveryZoneFromAdministrativeArea(company.delivery_zones, context.companyAddress.administrativeAreaLevel2 || '');
  }, [company, context]);

  const productSubFamilies = data?.company_product_count_by_sub_family || [];
  const displayedCompanySocialTags = useRelevantSocialTags(company?.social_tags ?? [], { rules: { 1: 1, 2: 2 }, max: 2 });

  if (isLoading || !company) {
    return (
      <Sheet open={isOpen} width={400} onClose={close}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <Skeleton variant="rounded" height={600} sx={{ borderRadius: 2 }} />
        </Box>
      </Sheet>
    );
  }

  return (
    <Sheet
      onClose={close}
      width={400}
      open={isOpen}
      title={company.tradeName}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: 'column',
          '.Kheops-ServiceCard-card': {
            backgroundColor: 'surfaceContainer.main'
          }
        }}
      >
        <Box>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <SizedImage
              src={FileUtils.BuildCompanyImageUrl({
                photo: company.photos?.[0]?.photo,
                type: Business_Profile_Enum.Supplier,                
              })}
              borderRadius={3}
              alt={t('common:company')}
              width="100%"
              sx={{
                aspectRatio: '4 / 3',
                '& img' : {
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover'
                }
              }} 
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
              }}
            >
              <WishlistSupplierButton supplierId={companyId} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <InfoSocialTags
              isWholesaler={company.is_wholesaler}
              minimumOrderValue={mov}
              deliveryTime={deliveryZone?.delivery_time || undefined}
              targetGeoLoc={targetGeoLoc!}
            />
          </Box>

          {!!productSubFamilies.length && (
            <Typography
              variant="bodySmall"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t(`product-family:${PackagingFamilyTypeIntl[productSubFamilies[0].sub_family as Product_Sub_Family_Name_Enum]}`)}
              {productSubFamilies.length > 1 && ` (+${productSubFamilies.length - 1})`}
            </Typography>
          )}

          {company.addresses?.[0] && (
            <Typography variant="bodyMedium" color="secondary" sx={{ mb: 1 }}>
              {formatCompanyAddress(company.addresses[0])}
            </Typography>
          )}
        </Box>

        {!!company.social_tags?.length && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {displayedCompanySocialTags.map((tag) => (
              <SocialTagCard key={tag.type} tag={tag} entity="SUPPLIER" />
            ))}
          </Box>
        )}
        <Divider flexItem />
        <CompanyServicesInfoLight
          services={company}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
            '.Kheops-CompanyServicesInfoLight-toggle': {
              width: '100%',
            }
          }}
        />
        <Divider flexItem />
        <DiscoveryDialogPackagings
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1.5,
            '& > div': {
              backgroundColor: 'surfaceContainerHigh.main',
              boxShadow: 'none',
              boxSizing: 'border-box',
              width: {xs: 148, sm: 164 },
              height: 'auto'
            },
          }}
          companyId={company.id}
        />        
      </Box>
      <Box
          sx={{
            position: 'sticky',
            bgcolor: 'surfaceContainerLow.main',
            bottom: -16,
            mx: -2,
            mb: -2,
            p: 2,
            mt: 'auto',
          }}
        >
          <Divider flexItem />
          <Box
            sx={{ display: 'flex', mt: 2, gap: 1, justifyContent: 'flex-end' }}
          >
            <Button onClick={() => navigate(generatePath(RoutePaths.CONTRACT_COMPANY, { companyId }))}>
              {t('common:see_more')}
            </Button>
            {contract && (
              <ResumeNegotiationButton contract={contract} />
            )}
            {!contract && (
              <ContractRequestButton
                companyId={company.id}
                buttonContent={t('contracts:contact_now')}
                buttonProps={{
                  sx: {
                    width: 'fit-content',
                  },
                }}
              />
            )}
          </Box>
        </Box>
    </Sheet>
  );
}
